/**
 * Centralised, validated access to build-time environment variables.
 *
 * Vite only exposes variables prefixed with VITE_ (see vite.config.ts /
 * .env.example). Reading them through this module — instead of
 * `import.meta.env.X` scattered across the app — means a missing variable
 * fails loudly and immediately, with a message that says exactly what to
 * fix, rather than surfacing as a confusing runtime error deep inside the
 * Supabase client.
 */

interface RequiredEnv {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
}

function readRequired(key: keyof RequiredEnv): string {
  const value = import.meta.env[key];
  if (!value || typeof value !== "string" || value.trim() === "") {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
        `Set it in .env.local for local development, or in the Cloudflare ` +
        `Pages project's Build environment variables for deployed builds. ` +
        `See .env.example for the full list.`,
    );
  }
  return value;
}

export const env = {
  supabaseUrl: readRequired("VITE_SUPABASE_URL"),
  supabaseAnonKey: readRequired("VITE_SUPABASE_ANON_KEY"),
  appName: (import.meta.env.VITE_APP_NAME as string | undefined) ?? "FI Radar",
  appEnv:
    (import.meta.env.VITE_APP_ENV as
      | "development"
      | "staging"
      | "production"
      | undefined) ?? "development",
  isProduction: import.meta.env.PROD,
} as const;
